import { useEffect, useLayoutEffect, useMemo } from "react";
import { App as AntdApp, ConfigProvider } from "antd";
import zhCN from "antd/locale/zh_CN";

import { adminModalConfig } from "./adminModalConfig";
import { AuthBootstrap } from "./auth/AuthBootstrap";
import { AdminRouterProvider, staticRoutes } from "./router";
import { buildMenuRoutes } from "./router/menuRoutes";
import { useUserStore } from "@/stores/react/authStore";
import { AdminTableEmpty } from "@/views/components/TableEmpty";

/** 在路由挂载前把服务端会话对应的菜单树写入 Zustand，供头部和侧栏共同消费。 */
function RouteMenuBootstrap() {
    const token = useUserStore(state => state.token);
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const menus = useMemo(() => (token ? buildMenuRoutes(staticRoutes, permissions) : []), [permissions, token]);

    useLayoutEffect(() => {
        useUserStore.getState().setMenuList(menus);
    }, [menus]);

    return null;
}

/** 订阅 API 层的全局错误事件，使用 AntdApp 上下文确保消息挂载到当前应用顶部。 */
function GlobalMessageBridge() {
    const { message: messageApi } = AntdApp.useApp();

    useEffect(() => {
        const handleGlobalMessage = (event: Event) => {
            const detail = (event as CustomEvent<{ message?: unknown; type?: string }>).detail;
            if (detail?.message == null || detail.message === "") return;
            const type =
                detail.type === "success" || detail.type === "warning" || detail.type === "info"
                    ? detail.type
                    : "error";
            messageApi.open({ type, content: String(detail.message) });
        };
        window.addEventListener("zhihao:global-message", handleGlobalMessage);
        return () => window.removeEventListener("zhihao:global-message", handleGlobalMessage);
    }, [messageApi]);

    return null;
}

/**
 * 管理后台根组件。
 *
 * 对齐旧版 App.vue 的全局 Element Plus 配置：按钮不自动插入中文字符间距，
 * 分页跳转文案保持中文；页面业务组件继续由 zhihao-ui-react 提供。
 */
export function App() {
    // 全局保留 Ant Form.Item 的必填标识，避免迁移页面因配置覆盖而丢失红色星号。
    return (
        <ConfigProvider
            locale={{ ...zhCN, Pagination: { ...zhCN.Pagination, jump_to: "转到" } }}
            renderEmpty={() => <AdminTableEmpty />}
            button={{ autoInsertSpace: false }}
            form={{ requiredMark: true, colon: false }}
            // 迁移页面统一继承旧版 Vue 确认框的居中位置，单个 Modal 显式配置仍可覆盖。
            modal={adminModalConfig}
        >
            <AntdApp>
                <GlobalMessageBridge />
                <AuthBootstrap>
                    <RouteMenuBootstrap />
                    <AdminRouterProvider />
                </AuthBootstrap>
            </AntdApp>
        </ConfigProvider>
    );
}

export default App;
